import React from "react";
import { MapPin, Clock, Train, Car, Calendar } from "lucide-react";
import { Reveal, Eyebrow, LotusDivider } from "../components/Decorative";
import PageHero from "../components/PageHero";
import Seo from "../components/seo/Seo";
import JsonLd from "../components/seo/JsonLd";
import { TextLink } from "../components/Bits";
import InterestForm from "../components/InterestForm";
import PosterFrame from "../components/PosterFrame";
import { ROUTES, breadcrumbs, pastDelhiEvents } from "../lib/seo";
import { LAST_EDITION, OG_POSTER } from "../lib/constants";
import { ShowCard } from "../components/Shows";
import { useUpcomingShows } from "../lib/shows";
import { EVENT_TITLE } from "../data/shows";
import { Link } from "react-router-dom";
import { getEdition, editionJsonLd } from "../lib/editions";
import { SITE_URL } from "../lib/constants";

const OCT = getEdition("23-october-2026");

const EXPECT = [
  "A live band held the drone and the groove while the whole hall sang the refrains.",
  "Two hours moved from quiet, grounding bhajans into full-throated collective singing and back to stillness.",
  "A multi-generational room — grandparents, couples, first-timers — with no dress code and no spectating.",
  "Premium sound and considered light in a proper seated auditorium, not a club and not a temple.",
];

export default function Delhi() {
  const delhiShow = useUpcomingShows().find((s) => s.id === "delhi");
  const mapEmbed =
    "https://www.google.com/maps?q=MPCU+Shah+Auditorium+Raj+Niwas+Marg+Civil+Lines+New+Delhi&output=embed";
  return (
    <>
      <Seo route={ROUTES.delhi} image={OG_POSTER} />
      <JsonLd data={[...editionJsonLd(OCT, `${SITE_URL}${ROUTES.editions.path}${OCT.slug}/`), ...pastDelhiEvents(), breadcrumbs([{ name: ROUTES.delhi.crumb, path: ROUTES.delhi.path }])]} />

      <PageHero
        eyebrow="Bhajan jamming in Delhi"
        title={<>Antara in <span className="gold-grad italic">Delhi</span></>}
        intro={`Three shows in Delhi so far: ${EVENT_TITLE} sold out the lawns of The Grand, Vasant Kunj on ${OCT.dateLabel}, and ${LAST_EDITION.note.charAt(0).toLowerCase() + LAST_EDITION.note.slice(1)} filled ${LAST_EDITION.venue}, Civil Lines, with two shows on ${LAST_EDITION.dateLabel}. If you've never been inside a bhajan jamming evening, `}
      >
        <p className="mt-3 font-body text-base md:text-lg" style={{ color: "#E8D9C5" }}>
          start with{" "}
          <TextLink to={ROUTES.pillar.path}><span style={{ color: "#E5C880" }}>what bhajan jamming actually is</span></TextLink>.
        </p>
      </PageHero>

      {/* The latest Delhi edition — Bhaav Sangam at The Grand */}
      <section className="bg-cream py-20 md:py-28 px-6" data-testid="delhi-october">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <Eyebrow>The latest Delhi edition</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-tight text-ink">
              {OCT.title} at {OCT.venue}
            </h2>
            <p className="mt-3 flex flex-wrap items-center gap-3 font-body text-sm text-ink-2/85">
              <span className="inline-flex items-center gap-2"><Calendar size={15} className="text-gold" /> {OCT.dateLabel}</span>
              <span className="inline-flex items-center gap-2"><MapPin size={15} className="text-gold" /> {OCT.venue}, New Delhi</span>
              <span className="rounded-full px-3 py-0.5 text-[11px] font-semibold tracking-[0.14em] uppercase sold-out-badge">Sold out</span>
            </p>
            <p className="mt-6 max-w-3xl font-body text-base md:text-lg text-ink-2/90 leading-relaxed">{OCT.recap}</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {OCT.photos.slice(0, 4).map((ph) => (
              <div key={ph.src} className="aspect-square overflow-hidden rounded-xl border border-gold/20">
                <img src={ph.src} alt={ph.alt} width={ph.width} height={ph.height} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
          <p className="mt-8 flex flex-wrap gap-x-8 gap-y-3 font-body text-sm">
            <Link to={`${ROUTES.editions.path}${OCT.slug}/`} className="text-amber underline underline-offset-4">Read the recap</Link>
            <Link to={ROUTES.gallery.path} className="text-amber underline underline-offset-4">See all the photographs</Link>
          </p>
        </div>
      </section>

      {/* When & where — dark */}
      <section className="bg-[#0F0A07] py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Eyebrow>The first Delhi edition · {LAST_EDITION.dateLabel}</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-tight" style={{ color: "#FBF6EE" }}>
              M.P.C.U. Shah<br />Auditorium
            </h2>
            <div className="mt-10 space-y-6" style={{ color: "#E8D9C5" }}>
              <div className="flex gap-4">
                <Calendar className="mt-1 flex-shrink-0" size={22} strokeWidth={1.3} style={{ color: "#C9A961" }} />
                <div>
                  <p className="eyebrow">It happened</p>
                  <p className="font-display text-xl mt-1" style={{ color: "#FBF6EE" }}>{LAST_EDITION.dateLabel}</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Clock className="mt-1 flex-shrink-0" size={22} strokeWidth={1.3} style={{ color: "#C9A961" }} />
                <div>
                  <p className="eyebrow">Two shows ran</p>
                  <p className="font-display text-xl mt-1" style={{ color: "#FBF6EE" }}>Matinee {LAST_EDITION.shows[0].time}</p>
                  <p className="font-display text-xl" style={{ color: "#FBF6EE" }}>Evening {LAST_EDITION.shows[1].time}</p>
                  <p className="font-body text-sm mt-1" style={{ color: "rgba(232,217,197,0.75)" }}>{LAST_EDITION.capacityPerShow} seats a show, both full.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <MapPin className="mt-1 flex-shrink-0" size={22} strokeWidth={1.3} style={{ color: "#C9A961" }} />
                <div>
                  <p className="eyebrow">Where</p>
                  <p className="font-display text-xl mt-1" style={{ color: "#FBF6EE" }}>{LAST_EDITION.venue}</p>
                  <p className="font-body text-sm" style={{ color: "rgba(232,217,197,0.75)" }}>{LAST_EDITION.venueStreet}, {LAST_EDITION.venueLocality} {LAST_EDITION.venuePostal}</p>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden border" style={{ borderColor: "rgba(201,169,97,0.3)" }}>
              <iframe src={mapEmbed} title="Shah Auditorium, Civil Lines — map" className="w-full h-full" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Getting there — light section */}
      <section className="bg-cream py-24 md:py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <Eyebrow>Shah Auditorium</Eyebrow>
              <h2 className="mt-4 font-display text-4xl md:text-5xl text-ink">The venue, for next time</h2>
              <p className="mt-5 font-body text-base md:text-lg text-ink-2/85 leading-relaxed">
                Shah Auditorium sits on Raj Niwas Marg in Civil Lines, in the old, tree-lined heart of
                North Delhi — a short hop from the Yamuna and Kashmere Gate. Here's how people
                reached it, for whenever Antara returns to Delhi.
              </p>
            </div>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full p-7 rounded-2xl bg-ivory-2 border border-gold/25">
                <Train className="text-gold mb-4" size={26} strokeWidth={1.3} />
                <h3 className="font-display text-2xl text-ink">By metro</h3>
                <p className="mt-2 font-body text-sm md:text-base text-ink-2/85 leading-relaxed">
                  Civil Lines station on the Yellow Line is the closest stop — a short auto or e-rickshaw
                  ride to Raj Niwas Marg. Kashmere Gate (the interchange for the Red and Violet lines)
                  is the next-nearest and easy from most of the city.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full p-7 rounded-2xl bg-ivory-2 border border-gold/25">
                <Car className="text-gold mb-4" size={26} strokeWidth={1.3} />
                <h3 className="font-display text-2xl text-ink">By car or cab</h3>
                <p className="mt-2 font-body text-sm md:text-base text-ink-2/85 leading-relaxed">
                  Civil Lines is well connected by the Ring Road and the Outer Ring Road. Book a cab to
                  the Shah Auditorium gate on Raj Niwas Marg. Guests were asked to arrive about
                  30 minutes before their show, to be seated before the first bhajan.
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-2xl mx-auto text-center font-serif-italic text-amber">
              Shah Auditorium is a five-minute walk from Civil Lines metro station (Yellow Line).
              On-site parking is limited — with two shows in a day it was tightest between 5 and
              7 PM, and the metro was much the easier arrival.
            </p>
          </Reveal>
        </div>
      </section>

      {/* What to expect — dark */}
      <section className="bg-[#0F0A07] py-24 md:py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="text-center">
              <Eyebrow>What the evening was</Eyebrow>
              <h2 className="mt-4 font-display text-4xl md:text-5xl" style={{ color: "#FBF6EE" }}>
How the Delhi evening ran
              </h2>
            </div>
          </Reveal>
          <ul className="mt-12 space-y-5">
            {EXPECT.map((e, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <li className="flex gap-4 items-start">
                  <span className="mt-2 h-2 w-2 rounded-full flex-shrink-0" style={{ background: "#C9A961" }} />
                  <p className="font-body text-base md:text-lg leading-relaxed" style={{ color: "#E8D9C5" }}>{e}</p>
                </li>
              </Reveal>
            ))}
          </ul>
          <Reveal delay={0.2}>
            <div className="mt-14 flex flex-col items-center">
              {delhiShow ? (
                <div className="w-full max-w-md" data-testid="delhi-next-show">
                  <p className="eyebrow text-center mb-4">Back in Delhi · {EVENT_TITLE}</p>
                  <ShowCard show={delhiShow} />
                </div>
              ) : (
                <>
                  <PosterFrame className="mb-10" maxW={300} />
                  <InterestForm tone="dark" heading={<>Want Antara back in <span className="gold-grad italic">Delhi</span>?</>} />
                </>
              )}
              <p className="mt-6 font-body text-sm" style={{ color: "rgba(232,217,197,0.7)" }}>
                Antara has played <TextLink to={ROUTES.gurgaon.path}><span style={{ color: "#E5C880" }}>Gurgaon</span></TextLink> before —
                see how those evenings went in the{" "}
                <TextLink to={ROUTES.editions.path}><span style={{ color: "#E5C880" }}>editions archive</span></TextLink>.
              </p>
            </div>
          </Reveal>
          <LotusDivider />
        </div>
      </section>
    </>
  );
}
